import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";

import IssueList from "../Issues/IssueList";
import { NOT_ATTRIBUTED } from "../Issues/checks";
import { formatPivotDay } from "../common/trafficEstimates";
import { formatDay } from "../Issues/dates";
import { readingOn } from "./figures";
import { changePointDayOf, changePointOf, leadOf, startedOn } from "./markers";
import { Card } from "./Card";

const compact = new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 });

/** The signals Issues reads a loss on, as its rows show them. */
const LOSSES = [
    { signal: "sessions", label: "sessions", available: (site) => site.source === "sessions" },
    { signal: "impressions", label: "impressions", available: (site) => site.searchConsole },
];

/**
 * What the Issues row says about the page, for the issue start the chart is centred on: its issues, the
 * crawl that found the most critical one, linking to the Monitored URI page on that day, and the loss on each
 * signal with Pettitt's change point on it.
 */
export default function Summary({ page, site, issue, since, pageRoute }) {
    const crawl = issue ? leadOf(startedOn(page.issues, since)).foundBy : null;
    const losses = issue ? LOSSES.filter(({ available }) => available(site)) : [];
    return (
        <Card>
            <Grid>
                <div>
                    <Heading>Issues</Heading>
                    <IssueList issues={page.issues} />
                    {crawl && <Found>Found by the crawl of <Link to={`${pageRoute}?date=${crawl.day}`}>{formatDay(crawl.day)}</Link></Found>}
                </div>

                {losses.length > 0 && (
                    <div>
                        <Heading>Est. loss per month, issue of {formatPivotDay(since)}</Heading>
                        {losses.map(({ signal, label }) => {
                            const reading = readingOn(issue, signal);
                            const changePoint = changePointOf(issue, signal);
                            return (
                                <Signal key={signal}>
                                    {reading?.lost > 0
                                        ? <Figure>~{compact.format(Math.round(reading.lost * 30))}</Figure>
                                        : <Muted>{NOT_ATTRIBUTED[reading?.lossCheck] || "no loss"}</Muted>}
                                    {reading?.rank && <Rank>#{reading.rank}</Rank>}
                                    <Name>{label}</Name>
                                    {changePoint && <ChangePoint>Pettitt change point {changePointDayOf(changePoint, since)}</ChangePoint>}
                                </Signal>
                            );
                        })}
                    </div>
                )}
            </Grid>
        </Card>
    );
}

const Grid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
    gap: 24px;
    align-items: start;
    @media (max-width: 900px) { grid-template-columns: minmax(0, 1fr); gap: 14px; }
`;

const Heading = styled.div`
    margin-bottom: 8px;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #94a3b8;
`;

const Signal = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 6px;
`;

const Figure = styled.span`
    font-size: 0.9rem;
    font-weight: 700;
    color: #0f172a;
`;

const Rank = styled.span`
    font-size: 0.72rem;
    font-weight: 700;
    color: #4f46e5;
`;

const Name = styled.span`
    font-size: 0.7rem;
    color: #94a3b8;
`;

const ChangePoint = styled.span`
    width: 100%;
    font-size: 0.66rem;
    color: #64748b;
`;

const Found = styled.div`
    margin-top: 8px;
    font-size: 0.7rem;
    color: #64748b;
    a { color: #4f46e5; font-weight: 600; text-decoration: none; }
    a:hover { text-decoration: underline; }
`;

const Muted = styled.span`
    font-size: 0.7rem;
    color: #a9b4c2;
`;
